/**
 * Banker Portal — First-time password setup via invite token.
 * Token can be passed via `?token=...` query string.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { KeyRound, Loader2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function BankerSetup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = params.get('token');
    if (t) setToken(t);
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/api/banker/auth/setup-password`, { token, password });
      localStorage.setItem('banker_token', r.data.token);
      localStorage.setItem('banker_profile', JSON.stringify(r.data.banker));
      toast.success('Account activated');
      navigate('/banker/inbox');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="banker-setup-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-700">
            <KeyRound className="w-7 h-7" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Activate Banker Account</h1>
          <p className="text-sm text-gray-500">Use the invite token your admin sent you to set a password.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Invite Token</Label>
            <Input value={token} onChange={e => setToken(e.target.value)} required data-testid="banker-setup-token" />
          </div>
          <div>
            <Label>New Password (min 8)</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="banker-setup-password" />
          </div>
          <div>
            <Label>Confirm Password</Label>
            <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required data-testid="banker-setup-confirm" />
          </div>
          <Button type="submit" disabled={busy} className="w-full" data-testid="banker-setup-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activate account'}
          </Button>
        </form>
        <p className="text-xs text-center text-gray-500">
          Already activated?{' '}
          <Link to="/banker/login" className="text-indigo-600 hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
